import appConfig from '../../config/appConfig';
import * as iccMethods from '../../consts/icc/iccMethods';
import {
    setConsumerAcceptedTnc,
    setConsumerAggregatedSettings,
    setConsumerData,
    setConsumerProducts,
    setConsumerProductsStatus,
    setConsumerTnc,
} from '../../state/ducks/consumer';
import {dispatch} from '../../state/store';
import arrayUtils from '../../utils/arrayUtils';
import urlUtils from '../../utils/urlUtils';
import authDataService from '../auth/authDataService';
import {
    aggregatedSettingsMapping,
    consumerMapping,
    consumerProductsInfoMapping,
    consumerProductsMapping,
    consumerProductsStatusMapping,
    deviceRegistrationMapping,
    deviceUnregistrationMapping,
    externalConsumerMapping,
    mediaUrlsMapping,
    tncMapping,
} from '../mapping/iccMarketMappings';
import httpStatusTypesService from '../server/httpStatusTypesService';
import server from '../server/server';
import iccResponseErrorCheckService from './iccResponseErrorCheckService';

const getIccMarketApiUrl = (methodName) => urlUtils.join(appConfig.getIccApiUrl(), methodName);

const getConsumerAggregatedSettings = (throwException = true) =>
    callGet({
        methodName: iccMethods.GET_CONSUMER_AGGREGATED_SETTINGS,
        mapper: aggregatedSettingsMapping,
        action: setConsumerAggregatedSettings,
        throwException,
    });

const getConsumerProfile = () =>
    callGet({
        methodName: iccMethods.GET_CONSUMER,
        mapper: consumerMapping,
        action: setConsumerData,
        throwException: true,
    });

const getExternalConsumerProfile = () =>
    callGet({
        methodName: iccMethods.GET_EXTERNAL_CONSUMER,
        mapper: externalConsumerMapping,
        action: setConsumerData,
        throwException: true,
    });

const getConsumerProducts = (gigyaToken) =>
    callGet({
        methodName: iccMethods.CONSUMER_PRODUCTS,
        headers: {et: gigyaToken},
        mapper: consumerProductsMapping,
        action: setConsumerProducts,
    });

const getConsumerProductsStatus = () =>
    callGet({
        methodName: iccMethods.GET_CONSUMER_PRODUCTS_STATUS,
        mapper: consumerProductsStatusMapping,
        action: setConsumerProductsStatus,
    });

const getMediaUrl = ({mediaNameList, throwException = false, isV1 = false}) => {
    if (mediaNameList) {
        return callPost({
            methodName: isV1 ? iccMethods.GET_MEDIA_URL : iccMethods.GET_MEDIA_URL_V2,
            requestConfig: {data: {mediaName: arrayUtils.toArray(mediaNameList)}},
            mapper: mediaUrlsMapping,
            throwException,
            isAnonymousUserAllowed: true,
        });
    }
};

const getProductInfoList = ({serialNumbers, throwException}) =>
    callPost({
        methodName: iccMethods.GET_PRODUCT_INFO_LIST,
        requestConfig: {data: {serialNumbers}},
        mapper: consumerProductsInfoMapping,
        throwException,
    });

const getProductInfoListV2 = ({codentifyIds, throwException}) =>
    callPost({
        methodName: iccMethods.GET_PRODUCT_INFO_LIST_V2,
        requestConfig: {data: {CodentifyID: codentifyIds}},
        mapper: consumerProductsInfoMapping,
        throwException,
    });

const setConsumerProductName = ({productName, deviceSerialNumber, throwException}) =>
    callPost({
        methodName: iccMethods.SET_CONSUMER_PRODUCT_NAME,
        requestConfig: {
            data: {
                productName,
                deviceSerialNumber,
            },
        },
        throwException,
    });

const getTnc = () =>
    callGet({
        methodName: iccMethods.GET_TNC,
        mapper: tncMapping,
        action: setConsumerTnc,
        throwException: true,
    });

const setTnc = ({data}) =>
    callPost({
        methodName: iccMethods.SET_CONSUMER_TNC,
        requestConfig: {data},
        throwException: true,
    });

const declineTnc = ({data, gigyaToken}) =>
    callDelete({
        methodName: iccMethods.SET_CONSUMER_TNC,
        requestConfig: {data, headers: {et: gigyaToken}},
        throwException: true,
    });

const getAcceptedTnc = () =>
    callGet({
        methodName: iccMethods.GET_ACCEPTED_TNC,
        mapper: tncMapping,
        action: setConsumerAcceptedTnc,
        params: {
            includeDeclined: true,
        },
        throwException: true,
    });

const setConsumerProductV2 = ({codentify, purchasedOn}) =>
    callPost({
        methodName: iccMethods.CONSUMER_PRODUCTS_V2,
        requestConfig: {params: {codentifyId: codentify, purchasedOn}},
        mapper: deviceRegistrationMapping,
        throwException: true,
    });

const setConsumerProductV3 = ({deviceSerialNumber, purchasedOn}) =>
    callPost({
        methodName: iccMethods.CONSUMER_PRODUCTS_V3,
        requestConfig: {params: {deviceSerialNumber, purchasedOn}},
        mapper: deviceRegistrationMapping,
        throwException: true,
    });

const removeConsumerProduct = ({data}) =>
    callDelete({
        methodName: iccMethods.CONSUMER_PRODUCTS,
        requestConfig: {params: {...data}},
        mapper: deviceUnregistrationMapping,
        throwException: true,
    });

const postStartConnectEvent = ({productId, marketIso, gigyaToken}) => {
    return callPost({
        methodName: iccMethods.START_CONNECT_TELEMETRY_EVENT,
        requestConfig: {
            data: {
                productId,
                marketIso,
                gigyaToken,
            },
        },
        throwException: true,
        isAnonymousUserAllowed: true,
    });
};

const callGet = ({methodName, mapper, action, params = {}, headers = {}, throwException, isAnonymousUserAllowed}) => {
    return callRequest({
        httpMethod: server.get,
        methodName,
        requestConfig: {params, headers},
        mapper,
        action,
        throwException,
        isAnonymousUserAllowed,
    });
};

const callPost = ({methodName, requestConfig, mapper, throwException, isAnonymousUserAllowed}) => {
    return callRequest({
        httpMethod: server.post,
        methodName,
        requestConfig,
        mapper,
        throwException,
        isAnonymousUserAllowed,
    });
};

const callDelete = ({methodName, requestConfig, mapper, throwException, isAnonymousUserAllowed}) => {
    return callRequest({
        httpMethod: server.requestDelete,
        methodName,
        requestConfig,
        mapper,
        throwException,
        isAnonymousUserAllowed,
    });
};

const callRequest = async ({
    httpMethod,
    methodName,
    requestConfig,
    mapper,
    action,
    throwException = false,
    isAnonymousUserAllowed,
}) => {
    try {
        const response = await callIccMethod(httpMethod, methodName, requestConfig, isAnonymousUserAllowed);

        const {data} = response;

        if (mapper) {
            const mappedData = mapper(data);

            if (action) {
                dispatch(action(mappedData));
            }

            return mappedData;
        } else {
            return httpMethod === server.get ? data : response;
        }
    } catch (e) {
        if (throwException) {
            return Promise.reject(e);
        }
    }
};

const callIccMethod = async (httpMethod, methodName, requestConfig = {}, isAnonymousUserAllowed) => {
    const methodUrl = getIccMarketApiUrl(methodName);

    try {
        return await callMethod(httpMethod, methodUrl, requestConfig, isAnonymousUserAllowed);
    } catch (e) {
        const isHandleError = geIsHandleIccError(e, methodName);

        await iccResponseErrorCheckService(e, isHandleError, isAnonymousUserAllowed);

        return await callMethod(httpMethod, methodUrl, requestConfig, isAnonymousUserAllowed);
    }
};

const callMethod = async (httpMethod, methodUrl, requestConfig, isAnonymousUserAllowed) => {
    const config = await getIccRequestConfig(requestConfig, isAnonymousUserAllowed);

    if (config) {
        const response = await httpMethod(methodUrl, config);

        return response;
    }

    throw new Error('ICC request config is undefined. See previous messages about details.');
};

const getIccRequestConfig = async (requestConfig, isAnonymousUserAllowed) => {
    try {
        const authToken = await authDataService.getAuthToken(isAnonymousUserAllowed);

        if (!authToken) return null;

        const appVersion = appConfig.getAppVersion();
        const {headers, ...rest} = requestConfig;

        const data = {
            headers: {Authorization: 'Bearer ' + authToken, v: appVersion, ...headers},
            ...rest,
        };

        return data;
    } catch (e) {
        return null;
    }
};

const geIsHandleIccError = (error, methodName) => {
    const errorStatus = error?.response?.status;
    const isGetProductInfoMethod =
        methodName === iccMethods.GET_PRODUCT_INFO_LIST || methodName === iccMethods.GET_PRODUCT_INFO_LIST_V2;
    const isGetMediaUrl = methodName === iccMethods.GET_MEDIA_URL_V2;
    const isGetMediaUrlUnavalible = isGetMediaUrl && httpStatusTypesService.isServiceUnavailable(errorStatus);

    return !(isGetProductInfoMethod || isGetMediaUrlUnavalible);
};

export default {
    declineTnc,
    getAcceptedTnc,
    getConsumerAggregatedSettings,
    getConsumerProducts,
    getConsumerProductsStatus,
    getConsumerProfile,
    getExternalConsumerProfile,
    getIccMarketApiUrl,
    getMediaUrl,
    getProductInfoList,
    getProductInfoListV2,
    getTnc,
    removeConsumerProduct,
    setConsumerProductName,
    setConsumerProductV2,
    setConsumerProductV3,
    setTnc,
    postStartConnectEvent,
};
